import { SubscriptionBasedComponent } from '../subscription-helpers/subscription-based.component';
import { Component, OnInit } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { LoadingService } from '../services/loading.service';
import { User } from '../model/user';
import * as globals from '../../globals/globals';
import UserHelper from '../helpers/user-helper';
import {
  ActivationEnd,
  ActivationStart,
  ChildActivationEnd,
  ChildActivationStart,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ResolveEnd,
  ResolveStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterEvent,
  RoutesRecognized,
  Scroll,
} from '@angular/router';
import { ModalService } from '../services/modal.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { RouteEventsService } from '../services/route-events-service';
import { ConnectionStatusService } from '../services/connection-status.service';
import * as moment from 'moment';
import * as _ from 'lodash';
import { AtelierService } from '../services/atelier-service';
import { StringHelper } from '../helpers/string-helper';
import { AtelierRecupEventService } from '../services/atelier/atelier-recup-event-service';
import { FileHelper } from '../helpers/file-helper';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'dd DD/MM',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'atelier-navbar',
  templateUrl: './atelier-navbar.component.html',
  styleUrls: ['./atelier-navbar.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class AtelierNavbarComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  user: User;
  date: Date;

  overview: boolean = true;
  activeView: AtelierView;

  ASSIGNMENTS_VIEW = AtelierView.ASSIGNMENTS;
  CONTROL_VIEW = AtelierView.CONTROL;
  RECOVERY_VIEW = AtelierView.RECOVERY;

  connectedToBackend: boolean = true;
  isImage = FileHelper.isImage;

  constructor(
    public loadingService: LoadingService,
    private authService: AuthService,
    private modalService: ModalService,
    private connectionStatusService: ConnectionStatusService,
    private router: Router,
    private routeEventsService: RouteEventsService,
    private atelierService: AtelierService,
    private atelierRecupEventService: AtelierRecupEventService
  ) {
    super();
  }

  ngOnInit() {
    this.initializeRouteProcessing();
    const previousRouteUrl = this.routeEventsService.previousRoutePath.value;
    if (previousRouteUrl.includes('/atelier/')) {
      const id = previousRouteUrl.split('/')[2];
      this.processOpen(id == null ? null : Number(id));
    }
    this.addSubscription(this.router.events, (event: any) => {
      if (event.routerEvent) {
        event = event.routerEvent;
      }
      if (event instanceof NavigationEnd) {
        const url = event.url;
        if (url.includes('/atelier/')) {
          const id = url.split('/')[2];
          this.processOpen(id == null ? null : Number(id));
        } else if (url.includes('/atelier-sign')) {
          this.overview = false;
        } else if (url.includes('/atelier')) {
          this.overview = true;
        }
      }
    });
    this.addSubscription(
      this.authService.getAuthenticatedUserSubscribable(),
      (user) => {
        this.user = user;
      }
    );
    this.addSubscription(
      this.connectionStatusService.getConnectionStatusSubject(),
      (online) => {
        this.connectedToBackend = online;
      }
    );

    const earlierStoredDate = this.atelierService
      .getSelectedAtelierDateSubscribable()
      .getValue();
    this.date = earlierStoredDate == null ? new Date() : earlierStoredDate;
  }

  processOpen(projectId: number) {
    if (projectId != null) {
      this.overview = false;
    }
  }

  private initializeRouteProcessing(): void {
    if (!StringHelper.isEmpty(this.router.url)) {
      this.processUrl(this.router.url);
    }
    this.addSubscription(
      this.router.events,
      (
        event:
          | RouterEvent
          | NavigationStart
          | NavigationEnd
          | NavigationCancel
          | NavigationError
          | RoutesRecognized
          | GuardsCheckStart
          | GuardsCheckEnd
          | RouteConfigLoadStart
          | RouteConfigLoadEnd
          | ChildActivationStart
          | ChildActivationEnd
          | ActivationStart
          | ActivationEnd
          | Scroll
          | ResolveStart
          | ResolveEnd
      ) => {
        if (event instanceof NavigationEnd) {
          this.processUrl(event.url);
        }
      }
    );
  }

  private processUrl(url: string) {
    switch (true) {
      case url.includes('/atelier-recup'):
        this.activeView = AtelierView.RECOVERY;
        break;
      case url.includes('/atelier-controle'):
        this.activeView = AtelierView.CONTROL;
        break;
      default:
        this.activeView = AtelierView.ASSIGNMENTS;
        break;
    }
  }

  dateChange(date: moment.Moment) {
    this.date = date.toDate();
    this.atelierService.setSelectedAtelierDate(this.date);
  }

  changeDay(amount: number) {
    const newDate = moment(this.date).add(amount, 'days');
    this.date = newDate.toDate();
    this.atelierService.setSelectedAtelierDate(this.date);
  }

  getLettres() {
    if (this.user == null) {
      return '';
    }
    return UserHelper.getLettres(this.user);
  }

  isAdmin(): boolean {
    if (this.user == null || _.isEmpty(this.user)) {
      return false;
    }
    return UserHelper.isAdmin(this.user);
  }

  isImpersonating(): boolean {
    return this.authService.isImpersonating();
  }

  public stopImpersonate() {
    this.modalService.confirmDialog(
      'Nabootsing stoppen',
      'Bent u zeker dat u de nabootsing wilt stoppen?',
      (b) => {
        if (b) {
          this.authService.stopImpersonate();
        }
      }
    );
  }

  returnToAdmin() {
    this.modalService.confirmDialog(
      'Keer terug naar adminportaal',
      'Bent u zeker dat u wilt terugkeren naar het adminportaal?',
      (b) => {
        if (b) {
          this.router.navigateByUrl('/projecten');
        }
      }
    );
  }

  isTestEnvironment(): boolean {
    return !globals.production;
  }

  backPressed() {
    this.router.navigateByUrl('/atelier');
  }

  openAssignments(): void {
    this.router.navigate(['atelier']);
  }

  openControle(): void {
    this.router.navigate(['atelier-controle']);
  }

  openRecovery(): void {
    this.atelierRecupEventService.getRecupDetailCloseSubject().next();
    this.router.navigate(['atelier-recup']);
  }

  public logout() {
    this.authService.logout();
    this.router.navigateByUrl('/login');
  }
}

enum AtelierView {
  ASSIGNMENTS,
  CONTROL,
  RECOVERY,
}
